import { useState } from 'react';
import { CalendarClock, ClipboardCheck, ShieldAlert, ShieldCheck } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { formatSydneyDateShort } from '../../../utils/helpers';
import { HrBarList } from '../components/BarList';
import { HrDataTable } from '../components/DataTable';
import type { HrColumn } from '../components/DataTable';
import { HrKpiGrid } from '../components/KpiGrid';
import { HrStatusPill } from '../components/StatusPill';
import {
  countBy,
  percent,
  signoffStatusTone,
  sortCountsDesc,
} from '../hr-dashboard-utils';
import type { HrPolicySignoff } from '../../../types/hr-dashboard.types';
import type { HrSectionProps } from './types';

export function PoliciesSection({
  data,
  people,
  filters,
  asOf,
  canExport,
  checkExport,
  toggleDept,
}: HrSectionProps) {
  const [selectedPolicy, setSelectedPolicy] = useState<string | null>(null);

  const ids = new Set(people.map((person) => person.id));
  const signoffs = data.policySignoffs.filter((row) => ids.has(row.userId));
  const activePolicies = data.policies.filter((policy) => policy.status === 'Active');
  const completed = signoffs.filter((row) => row.status === 'Completed').length;
  const signoffRate = percent(completed, signoffs.length);
  const overdue = signoffs.filter((row) => row.status === 'Overdue');
  const reviewsOverdue = data.policies.filter((policy) => policy.reviewOverdue);
  const unassigned = activePolicies.filter((policy) => policy.assigned === 0);

  const selectedSignoffs = selectedPolicy
    ? signoffs.filter((row) => row.policy === selectedPolicy)
    : signoffs;

  const departmentRates = sortCountsDesc(
    countBy(selectedSignoffs, (row) => row.dept)
  )
    .map(([label]) => {
      const rows = selectedSignoffs.filter((row) => (row.dept || 'Not recorded') === label);
      const done = rows.filter((row) => row.status === 'Completed').length;
      return {
        key: label,
        label,
        value: percent(done, rows.length),
        color:
          percent(done, rows.length) >= 80
            ? 'var(--success)'
            : percent(done, rows.length) >= 40
              ? 'var(--warning)'
              : 'var(--destructive)',
        hint: `${done} of ${rows.length} signed (${percent(done, rows.length)}%)`,
      };
    })
    .sort((left, right) => left.value - right.value);

  const columns: HrColumn<HrPolicySignoff>[] = [
    { key: 'user', header: 'Employee', value: (row) => row.user },
    { key: 'dept', header: 'Department', value: (row) => row.dept || '—' },
    { key: 'policy', header: 'Policy', value: (row) => row.policy },
    {
      key: 'due',
      header: 'Due',
      value: (row) => row.due || '',
      render: (row) => formatSydneyDateShort(row.due) || '—',
    },
    {
      key: 'status',
      header: 'Status',
      value: (row) => row.status,
      render: (row) => (
        <HrStatusPill tone={signoffStatusTone(row.status)}>{row.status}</HrStatusPill>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <HrKpiGrid
        items={[
          {
            label: 'Active policies',
            value: activePolicies.length,
            detail: `${data.policies.filter((policy) => policy.status === 'Draft').length} in draft`,
            icon: <ClipboardCheck className="h-5 w-5" />,
          },
          {
            label: 'Assigned to staff',
            value: activePolicies.filter((policy) => policy.assigned > 0).length,
            detail: `${unassigned.length} active policies unassigned`,
            tone: unassigned.length > 0 ? 'warning' : 'success',
            icon: <ShieldCheck className="h-5 w-5" />,
          },
          {
            label: 'Sign-off rate',
            value: signoffRate,
            unit: '%',
            detail: `${completed} of ${signoffs.length} assignments`,
            tone: signoffRate < 60 ? 'danger' : 'success',
            icon: <ShieldCheck className="h-5 w-5" />,
          },
          {
            label: 'Overdue sign-offs',
            value: overdue.length,
            detail: 'Past the due date',
            tone: overdue.length > 0 ? 'danger' : 'success',
            icon: <ShieldAlert className="h-5 w-5" />,
          },
          {
            label: 'Reviews overdue',
            value: reviewsOverdue.length,
            tone: reviewsOverdue.length > 0 ? 'warning' : 'success',
            icon: <CalendarClock className="h-5 w-5" />,
          },
        ]}
      />

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Policy library</CardTitle>
          <CardDescription>
            Select a policy to focus the chart and table below
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {data.policies
              .slice()
              .sort((left, right) => right.assigned - left.assigned)
              .map((policy) => {
                const rows = signoffs.filter((row) => row.policy === policy.title);
                const done = rows.filter((row) => row.status === 'Completed').length;
                const rate = rows.length ? percent(done, rows.length) : null;
                const active = selectedPolicy === policy.title;

                return (
                  <button
                    key={policy.id}
                    type="button"
                    onClick={() => setSelectedPolicy(active ? null : policy.title)}
                    className={`flex flex-col gap-3 rounded-[10px] border p-3 text-left transition-colors hover:border-link ${
                      active ? 'border-link bg-selection' : 'border-border bg-card'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span
                        className="grid h-14 w-14 shrink-0 place-items-center rounded-full"
                        style={{
                          background:
                            rate === null
                              ? 'var(--muted)'
                              : `conic-gradient(var(--chart-3) ${rate}%, var(--muted) 0)`,
                        }}
                        aria-hidden
                      >
                        <span className="grid h-10 w-10 place-items-center rounded-full bg-card text-xs font-semibold tabular-nums text-foreground">
                          {rate === null ? '—' : `${rate}%`}
                        </span>
                      </span>
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-foreground">
                          {policy.title}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          v{policy.version} · {rows.length ? `${done}/${rows.length} signed` : 'not assigned'}
                        </p>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-1.5">
                      <HrStatusPill tone={policy.status === 'Active' ? 'success' : 'muted'}>
                        {policy.status}
                      </HrStatusPill>
                      {policy.reviewOverdue ? (
                        <HrStatusPill tone="warning">Review overdue</HrStatusPill>
                      ) : policy.review ? (
                        <HrStatusPill tone="link">
                          Review {formatSydneyDateShort(policy.review)}
                        </HrStatusPill>
                      ) : null}
                      {policy.status === 'Active' && policy.assigned === 0 ? (
                        <HrStatusPill tone="warning">Unassigned</HrStatusPill>
                      ) : null}
                    </div>
                  </button>
                );
              })}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
        <Card className="lg:col-span-5">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Sign-off rate by department</CardTitle>
            <CardDescription>{selectedPolicy || 'All assigned policies'}</CardDescription>
          </CardHeader>
          <CardContent>
            <HrBarList
              items={departmentRates}
              max={100}
              formatter={(value) => `${value}%`}
              onSelect={toggleDept}
              selectedKeys={filters.dept}
            />
          </CardContent>
        </Card>

        <Card className="lg:col-span-7">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Outstanding sign-offs</CardTitle>
            <CardDescription>
              People to chase · {selectedPolicy || 'all policies'}
            </CardDescription>
          </CardHeader>
          <CardContent className="p-3">
            <HrDataTable
              rows={selectedSignoffs.filter((row) => row.status !== 'Completed')}
              columns={columns}
              initialSort={{ key: 'due', direction: 'asc' }}
              searchPlaceholder="Search employee or policy…"
              emptyLabel="Every assignment is signed off"
              viewName="Outstanding sign-offs"
              filters={filters}
              asOf={asOf}
              canExport={canExport}
              onBeforeExport={checkExport}
              rowKey={(row) => row.id}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
